import { patientDetailsRoutes } from '@/pages/patient-details/routes';
import { Role } from '@/shared/lib/types/admin';
import { useCompanyStore } from '@/shared/model';
import BreadcrumbLink from '@/shared/ui/components/BreadcrumbLink';
import { ComingSoon } from '@/shared/ui/components/ComingSoon';
import RequireRole from '@/shared/ui/components/RequireRole';
import type { UIMatch } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import type { RoutePathDefinition } from '../../types';

const CompanyNameBreadcrumb = () => {
  const { company } = useCompanyStore();
  return <BreadcrumbLink>{company?.name || '...'}</BreadcrumbLink>;
};

export const adminSectionRoutes: RoutePathDefinition[] = [
  {
    path: 'timesheets',
    lazy: () => import('@/pages/administration/ui/timesheets').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Timesheets</BreadcrumbLink> }
  },
  {
    path: 'invoices/:id',
    lazy: () => import('@/pages/administration/ui/invoices/details').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Invoices</BreadcrumbLink> }
  },
  {
    path: 'invoices',
    lazy: () => import('@/pages/administration/ui/invoices').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Invoices</BreadcrumbLink> }
  },
  {
    path: 'dashboard',
    lazy: () => import('@/pages/administration/ui/dashboard').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Dashboard</BreadcrumbLink> }
  },
  {
    path: 'organizations/:id',
    handle: { crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/patients'}>Organization</BreadcrumbLink> },
    lazy: () => import('@/pages/administration/ui/organizations/detail').then(m => ({ Component: m.default })),
    children: [
      {
        path: 'patients',
        handle: { crumb: () => <BreadcrumbLink>Patients</BreadcrumbLink> },
        lazy: () => import('@/pages/administration/ui/organizations/detail/patients').then(m => ({ Component: m.default }))
      },
      {
        path: 'stores',
        handle: { crumb: () => <BreadcrumbLink>Stores</BreadcrumbLink> },
        lazy: () => import('@/pages/administration/ui/organizations/detail/stores').then(m => ({ Component: m.default }))
      },
      {
        path: 'tasks',
        handle: { crumb: () => <BreadcrumbLink>Tasks</BreadcrumbLink> },
        lazy: () => import('@/pages/administration/ui/organizations/detail/tasks').then(m => ({ Component: m.default }))
      }
    ]
  },
  {
    path: 'organizations/:id/stores/:id',
    handle: { crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/patients'}>{data.params.id}</BreadcrumbLink> },
    lazy: () => import('@/pages/administration/ui/organizations/store').then(m => ({ Component: m.default })),
    children: [
      {
        path: 'patients',
        lazy: () => import('@/pages/administration/ui/organizations/store/patients').then(m => ({ Component: m.default })),
        handle: { crumb: () => <BreadcrumbLink>Patients</BreadcrumbLink> }
      },
      {
        path: 'tasks',
        lazy: () => import('@/pages/administration/ui/organizations/store/tasks').then(m => ({ Component: m.default })),
        handle: { crumb: () => <BreadcrumbLink>Tasks</BreadcrumbLink> }
      }
    ]
  },
  {
    path: 'organizations',
    handle: { crumb: () => <BreadcrumbLink>Organizations</BreadcrumbLink> },
    lazy: () => import('@/pages/administration/ui/organizations').then(m => ({ Component: m.default }))
  },
  {
    path: 'users',
    handle: { crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname}>Users</BreadcrumbLink> },
    lazy: () => import('@/pages/administration/ui/users').then(m => ({ Component: m.default })),
    children: [
      {
        path: '',
        lazy: () => import('@/pages/administration/ui/users/list').then(m => ({ Component: m.default })),
        handle: { crumb: null }
      },
      {
        path: ':id/details',
        lazy: () => import('@/pages/administration/ui/users/details').then(m => ({ Component: m.default })),
        handle: { crumb: (data: UIMatch) => <BreadcrumbLink>{data.params.id}</BreadcrumbLink> }
      }
    ]
  },
  {
    path: 'patients',
    lazy: () => import('@/pages/administration/ui/patients').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Patients</BreadcrumbLink> }
  },
  {
    path: 'health-plans',
    lazy: () => import('@/pages/administration/ui/health-plans').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Health Plans</BreadcrumbLink> }
  },
  {
    path: 'insurance-plans',
    lazy: () => import('@/pages/administration/ui/insurance-plans').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Insurance Plans</BreadcrumbLink> }
  },
  {
    path: 'resources',
    lazy: () => import('@/pages/administration/ui/resources').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Resources</BreadcrumbLink> }
  },
  {
    path: 'providers',
    handle: { crumb: () => <BreadcrumbLink>Providers</BreadcrumbLink> },
    children: [
      { path: '', element: <Navigate to="directory" replace /> },
      {
        path: 'directory',
        lazy: () => import('@/pages/administration/ui/providers/directory').then(m => ({ Component: m.default })),
        handle: { crumb: () => <BreadcrumbLink>Directory</BreadcrumbLink> }
      },
      {
        path: 'availability',
        lazy: () => import('@/pages/administration/ui/providers/availability').then(m => ({ Component: m.default })),
        handle: { crumb: () => <BreadcrumbLink>Availability</BreadcrumbLink> }
      }
    ]
  },
  {
    path: 'logs',
    lazy: () => import('@/pages/administration/ui/logs').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Logs</BreadcrumbLink> }
  },
  {
    path: 'my-performance',
    lazy: () => import('@/pages/administration/ui/my-performance').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>My Performance</BreadcrumbLink> }
  },
  {
    path: 'announcements',
    lazy: () => import('@/pages/administration/ui/announcements').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Announcements</BreadcrumbLink> }
  },
  {
    path: 'rx-event-metrics',
    lazy: () => import('@/pages/administration/ui/rx-event-metrics').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Rx Event Metrics</BreadcrumbLink> }
  },
  {
    path: 'system',
    lazy: () => import('@/pages/administration').then(m => ({ Component: m.SystemSection })),
    handle: { crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname + '/notification-files'}>System</BreadcrumbLink> },
    children: [
      {
        path: 'notification-files',
        Component: () => <ComingSoon title="Notification Files" />,
        handle: { crumb: () => <BreadcrumbLink>Notification Files</BreadcrumbLink> }
      },
      {
        path: 'configurations',
        // Super Admin only (z93r0b76qr); the page's own role check stays as a second line.
        lazy: () =>
          import('@/pages/administration/ui/System/Configurations').then(m => ({
            Component: () => (
              <RequireRole roles={[Role.SUPER]}>
                <m.default />
              </RequireRole>
            )
          })),
        handle: { crumb: () => <BreadcrumbLink>Configurations</BreadcrumbLink> }
      },
      {
        path: 'companies',
        handle: { crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname}>Pharmacies</BreadcrumbLink> },
        children: [
          {
            path: '',
            lazy: () => import('@/pages/administration/ui/System/AllCompanies').then(m => ({ Component: m.default })),
            handle: { crumb: () => <BreadcrumbLink>All</BreadcrumbLink> }
          },
          {
            path: ':rpid',
            lazy: () => import('@/pages/administration/ui/company').then(m => ({ Component: m.default })),
            handle: { crumb: () => <CompanyNameBreadcrumb /> }
          }
        ]
      },
      {
        path: 'eligibility-checks',
        // PSM (Admin) and Super Admin only (z93r0b9nj2); the backend gates every query and mutation too.
        lazy: () =>
          import('@/pages/administration/ui/System/EligibilityChecks').then(m => ({
            Component: () => (
              <RequireRole roles={[Role.SUPER, Role.ADMIN]}>
                <m.default />
              </RequireRole>
            )
          })),
        handle: { crumb: () => <BreadcrumbLink>Eligibility Checks</BreadcrumbLink> }
      },
      {
        path: 'data-import',
        handle: { crumb: (data: UIMatch) => <BreadcrumbLink to={data.pathname}>Data Import</BreadcrumbLink> },
        children: [
          {
            path: '',
            lazy: () => import('@/pages/administration/ui/System/DataImport').then(m => ({ Component: m.default })),
            handle: { crumb: () => <BreadcrumbLink>Import</BreadcrumbLink> }
          },
          {
            path: 'runs/:runId',
            lazy: () => import('@/pages/administration/ui/System/DataImport/RunDetail').then(m => ({ Component: m.default })),
            handle: { crumb: (data: UIMatch) => <BreadcrumbLink>{data.params.runId}</BreadcrumbLink> }
          },
          {
            path: 'rake-runs/:runId',
            lazy: () => import('@/pages/administration/ui/System/DataImport/RakeRunDetail').then(m => ({ Component: m.default })),
            handle: { crumb: (data: UIMatch) => <BreadcrumbLink>{data.params.runId}</BreadcrumbLink> }
          }
        ]
      }
    ]
  },
  {
    path: 'mock-apis',
    lazy: () => import('@/pages/administration/ui/mock-apis').then(m => ({ Component: m.default })),
    handle: { crumb: () => <BreadcrumbLink>Athena APIs</BreadcrumbLink> }
  },
  {
    path: 'shared',
    children: [
      {
        path: 'health-app/medications',
        lazy: patientDetailsRoutes.healthApp.medications,
        handle: { crumb: () => <BreadcrumbLink>Medications</BreadcrumbLink> }
      },
      {
        path: 'health-app/immunizations',
        lazy: patientDetailsRoutes.healthApp.immunizations,
        handle: { crumb: () => <BreadcrumbLink>Immunizations</BreadcrumbLink> }
      },
      {
        path: 'health-app/energy',
        lazy: patientDetailsRoutes.healthApp.energy,
        handle: { crumb: () => <BreadcrumbLink>Energy</BreadcrumbLink> }
      },
      {
        path: 'health-app/allergies',
        lazy: patientDetailsRoutes.healthApp.allergies,
        handle: { crumb: () => <BreadcrumbLink>Allergies</BreadcrumbLink> }
      },
      {
        path: 'health-app/vitals',
        lazy: patientDetailsRoutes.healthApp.vitals,
        handle: { crumb: () => <BreadcrumbLink>Vitals</BreadcrumbLink> }
      },
      {
        path: 'quickupload-history',
        handle: { crumb: () => <BreadcrumbLink>Quick Upload History</BreadcrumbLink> },
        lazy: patientDetailsRoutes.quickUpload.history,
        children: [
          {
            path: ':sessionId',
            lazy: patientDetailsRoutes.quickUpload.items,
            handle: { crumb: (data: UIMatch) => <BreadcrumbLink>{data.params.sessionId}</BreadcrumbLink> }
          }
        ]
      }
    ]
  }
];
